import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarRange, Copy, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { assumptionsService } from '@/services/demandPlanning/assumptions.service';
import { planningCyclesService } from '@/services/demandPlanning/planningCycles.service';
import { consensusOverviewQueryKeys, useConsensusCycleMeta } from '../hooks/useConsensusOverviewQueries';

import { AssumptionsPanel } from '../components/AssumptionsPanel';
import { SopConfirmDialog } from '../components/SopConfirmDialog';
import { PendingApprovalsPanel } from '../components/PendingApprovalsPanel';
import { CreatePlanningCycleForm } from '../components/CreatePlanningCycleForm';
import { HelpTip } from '../components/HelpTip';
import type { CreateAssumptionPayload, UpdateAssumptionPayload } from '../types/consensus.types';
import type { PlanningCycleStatus } from '../types/consensus.types';
import { demandPlanningPageClass } from '../utils/pageShell';
import { cycleStatusBadgeClass, planningCycleStatusLabel } from '../utils/planningCycleStatus';

import { formatUiDate } from '@/utils/uiLocale';

function formatDate(value: string, language?: string | null): string {
  return formatUiDate(value, { day: '2-digit', month: '2-digit', year: 'numeric' }, language);
}

function parseCycleId(raw: string | null): number | null {
  if (!raw) return null;
  const id = Number.parseInt(raw, 10);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function PlanningCyclesPage({ embedded = false }: { embedded?: boolean }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null);
  const [showCycleForm, setShowCycleForm] = useState(false);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);

  const cycleFromUrl = parseCycleId(searchParams.get('cycle'));

  const planningCycles = useQuery({
    queryKey: ['planning-cycles-list'],
    queryFn: () => planningCyclesService.list(),
  });

  const cycles = planningCycles.data ?? [];

  useEffect(() => {
    if (!cycles.length) return;
    const urlMatch = cycleFromUrl != null && cycles.some((c) => c.id === cycleFromUrl);
    if (urlMatch) {
      setSelectedCycleId(cycleFromUrl);
      return;
    }
    if (selectedCycleId != null && !cycles.some((c) => c.id === selectedCycleId)) {
      setSelectedCycleId(null);
    }
  }, [cycles, cycleFromUrl, selectedCycleId]);

  const selectCycle = (cycleId: number) => {
    setSelectedCycleId(cycleId);
    setSearchParams({ cycle: String(cycleId) }, { replace: true });
  };

  const overview = useConsensusCycleMeta(selectedCycleId);

  const createPlanningCycle = useMutation({
    mutationFn: (payload: Parameters<typeof planningCyclesService.createCycleWithVersion>[0]) =>
      planningCyclesService.createCycleWithVersion(payload),
    onSuccess: (cycleId) => {
      selectCycle(cycleId);
      void queryClient.invalidateQueries({ queryKey: ['planning-cycles-list'] });
      void queryClient.invalidateQueries({ queryKey: consensusOverviewQueryKeys.metaPrefix });
    },
  });

  const updateCycleStatus = useMutation({
    mutationFn: ({ cycleId, status }: { cycleId: number; status: PlanningCycleStatus }) =>
      planningCyclesService.updateStatus(cycleId, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['planning-cycles-list'] });
      void queryClient.invalidateQueries({ queryKey: consensusOverviewQueryKeys.metaPrefix });
    },
  });

  const invalidateOverview = () => {
    void queryClient.invalidateQueries({ queryKey: consensusOverviewQueryKeys.metaPrefix });
    void queryClient.invalidateQueries({ queryKey: consensusOverviewQueryKeys.chartPrefix });
  };

  const createAssumption = useMutation({
    mutationFn: (payload: CreateAssumptionPayload) =>
      assumptionsService.create(selectedCycleId!, payload),
    onSuccess: invalidateOverview,
  });

  const updateAssumption = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateAssumptionPayload }) =>
      assumptionsService.update(id, selectedCycleId!, payload),
    onSuccess: invalidateOverview,
  });

  const deleteAssumption = useMutation({
    mutationFn: (id: number) => assumptionsService.delete(id, selectedCycleId!),
    onSuccess: invalidateOverview,
  });

  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const syncForecastBootstrap = useMutation({
    mutationFn: (opts?: { force?: boolean }) =>
      planningCyclesService.syncForecastAndBootstrap(selectedCycleId!, opts),
    onMutate: () => {
      setSyncError(null);
      setSyncMessage(null);
    },
    onSuccess: (data) => {
      invalidateOverview();
      void queryClient.invalidateQueries({ queryKey: ['consensus-overview'] });
      setSyncMessage(
        t('demandPlanning.syncForecastSuccess', {
          forecastLines: data.forecast_lines_created,
          consensusLines: data.consensus_lines_created,
        }),
      );
    },
    onError: (error: Error & { status?: number }) => {
      if (error.status === 409) {
        setSyncError(t('demandPlanning.syncForecastInProgress'));
        return;
      }
      setSyncError(error.message || t('demandPlanning.syncForecastError'));
    },
  });

  const duplicateCycle = useMutation({
    mutationFn: () => {
      const source = cycles.find((c) => c.id === selectedCycleId);
      const defaultName = source ? `${source.name} (copia)` : undefined;
      return planningCyclesService.duplicate(selectedCycleId!, defaultName);
    },
    onSuccess: (newCycle) => {
      selectCycle(newCycle.id);
      void queryClient.invalidateQueries({ queryKey: ['planning-cycles-list'] });
      void queryClient.invalidateQueries({ queryKey: ['consensus-overview'] });
    },
  });

  const cycle = overview.data?.planning_cycle ?? null;
  const version = overview.data?.plan_version ?? null;
  const assumptions = overview.data?.assumptions ?? [];

  const planTabTo =
    selectedCycleId != null
      ? `/demand-planning/sop/plan?cycle=${selectedCycleId}`
      : '/demand-planning/sop/plan';

  return (
    <div className={`${demandPlanningPageClass(embedded)} pt-4 md:pt-5`}>
      {!embedded ? (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm text-gray-500">
              <Link to={planTabTo} className="text-stocker-primary hover:underline">
                {t('consensus.title')}
              </Link>
              {' / '}
              <span className="text-gray-700">{t('nav.planningCycles')}</span>
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-gray-900">{t('nav.planningCycles')}</h1>
            <p className="mt-1 text-sm text-gray-600">{t('sopCycles.subtitle')}</p>
          </div>
          <Link
            to="/demand-planning/sop/scenarios"
            className="text-sm text-stocker-primary hover:underline"
          >
            {t('nav.scenarios')}
          </Link>
        </div>
      ) : null}

      {(planningCycles.error || overview.error) && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {t('demandPlanning.backendError')}
          <button
            type="button"
            className="ml-3 font-medium underline"
            onClick={() => {
              void planningCycles.refetch();
              void overview.refetch();
            }}
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      <div className="grid w-full min-w-0 grid-cols-1 gap-6 xl:grid-cols-[min(100%,28rem)_minmax(0,1fr)] xl:items-start">
        <section className="flex w-full min-w-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm max-h-72 md:max-h-96 xl:max-h-[min(calc(100vh-8rem),36rem)]">
          <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <CalendarRange className="h-4 w-4 text-stocker-primary" />
              <h2 className="text-sm font-semibold text-gray-900">{t('sopCycles.historyTitle')}</h2>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => setShowCycleForm((v) => !v)}
            >
              <Plus className="h-3.5 w-3.5" />
              {t('consensus.newPlanningCycle')}
            </button>
          </div>

          {showCycleForm ? (
            <CreatePlanningCycleForm
              saving={createPlanningCycle.isPending}
              onCancel={() => setShowCycleForm(false)}
              onSubmit={async (payload) => {
                await createPlanningCycle.mutateAsync(payload);
                setShowCycleForm(false);
              }}
            />
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2">{t('consensus.cycleName')}</th>
                  <th className="px-3 py-2">{t('consensus.cycleStatusLabel')}</th>
                  <th className="px-3 py-2">{t('common.period')}</th>
                </tr>
              </thead>
              <tbody>
                {planningCycles.isLoading ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-8 text-center text-gray-500">
                      {t('common.loading')}
                    </td>
                  </tr>
                ) : cycles.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-8 text-center text-gray-500">
                      {t('sopCycles.noCycles')}
                    </td>
                  </tr>
                ) : (
                  cycles.map((row) => {
                    const selected = row.id === selectedCycleId;
                    return (
                      <tr
                        key={row.id}
                        className={`cursor-pointer border-t border-gray-100 hover:bg-stocker-primary/5 ${
                          selected ? 'bg-stocker-primary/10' : ''
                        }`}
                        onClick={() => selectCycle(row.id)}
                      >
                        <td className="px-3 py-2.5">
                          <div className="font-medium text-gray-900">{row.name}</div>
                          <div className="mt-0.5 text-[11px] text-gray-500">{row.cycle_type}</div>
                        </td>
                        <td className="px-3 py-2.5">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cycleStatusBadgeClass(row.status)}`}
                          >
                            {planningCycleStatusLabel(row.status, t)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-600">
                          {formatDate(row.period_start, i18n.language)} – {formatDate(row.period_end, i18n.language)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="min-w-0 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          {selectedCycleId == null ? (
            <p className="py-12 text-center text-sm text-gray-500">{t('sopCycles.selectCycleHint')}</p>
          ) : overview.isLoading ? (
            <p className="py-12 text-center text-sm text-gray-500">{t('common.loading')}</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{cycle?.name}</h2>
                  <p className="text-sm text-gray-500">
                    {overview.data?.plan_version.version_name} (v
                    {overview.data?.plan_version.version_number})
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-50 disabled:opacity-50"
                    disabled={duplicateCycle.isPending}
                    onClick={() => setShowDuplicateConfirm(true)}
                  >
                    <Copy className="h-3.5 w-3.5" />
                    {t('consensus.duplicateCycle')}
                  </button>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="rounded-md border border-stocker-primary px-3 py-1.5 text-xs font-medium text-stocker-primary hover:bg-stocker-primary/5 disabled:opacity-50"
                      disabled={syncForecastBootstrap.isPending}
                      onClick={() => void syncForecastBootstrap.mutateAsync({})}
                    >
                      {syncForecastBootstrap.isPending
                        ? t('common.loading')
                        : t('demandPlanning.syncForecastBootstrap')}
                    </button>
                    <HelpTip>{t('demandPlanning.help.syncBootstrap')}</HelpTip>
                  </div>
                  <label className="text-xs text-gray-600" htmlFor="cycle-status">
                    {t('consensus.cycleStatusLabel')}
                  </label>
                  <select
                    id="cycle-status"
                    className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                    value={cycle?.status ?? 'open'}
                    disabled={updateCycleStatus.isPending}
                    onChange={(e) => {
                      if (!cycle) return;
                      void updateCycleStatus.mutateAsync({
                        cycleId: cycle.id,
                        status: e.target.value as PlanningCycleStatus,
                      });
                    }}
                  >
                    {(
                      ['draft', 'open', 'in_review', 'approved', 'closed', 'cancelled'] as const
                    ).map((s) => (
                      <option key={s} value={s}>
                        {planningCycleStatusLabel(s, t)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {syncMessage ? (
                <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                  <p>{syncMessage}</p>
                  {selectedCycleId != null ? (
                    <Link
                      to={`/demand-planning/sop/plan?cycle=${selectedCycleId}`}
                      className="mt-2 inline-block font-medium text-green-900 underline hover:text-green-950"
                    >
                      {t('demandPlanning.syncForecastViewConsensus')}
                    </Link>
                  ) : null}
                </div>
              ) : null}

              {syncError ? (
                <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  {syncError}
                </p>
              ) : null}

              {syncForecastBootstrap.isPending ? (
                <p className="mt-3 text-sm text-gray-600">{t('demandPlanning.syncForecastPending')}</p>
              ) : null}

              <PendingApprovalsPanel planningCycleId={selectedCycleId} />

              {selectedCycleId != null &&
              (createAssumption.isPending ||
                updateAssumption.isPending ||
                deleteAssumption.isPending) ? (
                <p className="text-sm text-gray-600">{t('demandPlanning.rollupAssumptionPending')}</p>
              ) : null}

              {selectedCycleId != null && (
                <AssumptionsPanel
                  rows={assumptions}
                  planFamilies={overview.data?.plan_scope?.families}
                  cycleTitle={
                    version
                      ? `${cycle?.name ?? ''} · ${version.version_name} (#${version.version_number})`
                      : (cycle?.name ?? '')
                  }
                  loading={overview.isLoading}
                  saving={createAssumption.isPending || updateAssumption.isPending}
                  deleting={deleteAssumption.isPending}
                  onCreate={async (payload) => {
                    await createAssumption.mutateAsync(payload);
                  }}
                  onUpdate={async (id, payload) => {
                    await updateAssumption.mutateAsync({ id, payload });
                  }}
                  onDelete={async (id) => {
                    await deleteAssumption.mutateAsync(id);
                  }}
                />
              )}
            </>
          )}
        </section>
      </div>

      <SopConfirmDialog
        open={showDuplicateConfirm}
        title={t('consensus.duplicateCycle')}
        message={t('consensus.duplicateCycleConfirm', { name: cycle?.name ?? '' })}
        confirmLabel={t('consensus.duplicateCycle')}
        busy={duplicateCycle.isPending}
        onClose={() => setShowDuplicateConfirm(false)}
        onConfirm={async () => {
          await duplicateCycle.mutateAsync();
          setShowDuplicateConfirm(false);
        }}
      />
    </div>
  );
}
